import {createStore} from '@ngneat/elf';
import {deleteEntities, selectManyByPredicate, updateEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, forelderIdPredikat, foreldreIdPredikat, hentFraLokallagerEllerNull, leggTilForelderId, oppdaterEntitetMedForelder, opprettEllerOppdatertHendelse, registrerEntitetMedForelder, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForelder, slettAlleEntiteterMedForeldre, slettHendelse, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, Observable, Subject} from "rxjs";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sorterLevertSamling} from "felles/ng2/rxjsHjelpere";
import {sorterBeslutning} from "felles-ng2/modell/beslutningModellHjelpere";
import {AgendaelementId, AgendaelementModell, BeslutningId, BeslutningModell, BeslutningRessursClient, FavorittType, ModelloppdateringType, OppdaterBeslutningOgOppgaverSpesifikasjon, OppdateringmodellBeslutning, OpprettBeslutningSpesifikasjon} from "../ressurser/generertNg2HttpKlient";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {tap} from "rxjs/operators";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";
import {OppgaveDatatjeneste} from "felles-ng2/tjenester/datalager/OppgaveDatatjeneste";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {harTilgangTilSaksdetaljer} from "felles-ng2/modell/sakModellHjelpere";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";

const debug = initDebugger('BeslutningDatatjeneste');


/** Datalager for {@link BeslutningModell}. */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class BeslutningDatatjeneste {

    /** Lokalt datalager for {@link BeslutningModell} entiteter.
     * Bruker beslutning.sak som forelder.*/
      //public for testbarhet
    readonly beslutningLager = createStore({name: 'beslutning'}, withEntities<BeslutningModell, 'id'>());

    /** Holder rede på åpne subscriptions basert på beslutning.id. Benyttes ifbm modelloppdateringer. */
    readonly beslutningAbonnenter = new DatalagerAbonnenter<BeslutningId>('beslutning'); //public for testbarhet

    /** Holder rede på åpne subscriptions basert på sakId (beslutning.sakId). Benyttes ifbm modelloppdateringer. */
    readonly sakAbonnenter = new DatalagerAbonnenter<AgendaelementId>('beslutning.sak'); //public for testbarhet

    private readonly vedNyBeslutningSubject = new Subject<BeslutningModell>();

    // datatjenestene under må settes etter opprettelse av instans pga sirkularitet
    private sakDatatjeneste!: SakDatatjeneste;
    private oppgaveDatatjeneste!: OppgaveDatatjeneste;

    constructor(private beslutningRessurs: BeslutningRessursClient,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private kontekst: BrukerkontekstTilbyder) {

        this.propagerEndringerTilAndreDatatjenester();
        this.lyttTilModelloppdateringer();
    }

    /** For intern bruk. Datatjenestene under må settes etter opprettelse av instans pga sirkularitet */
    setDatatjenester(sakDatatjeneste: SakDatatjeneste, oppgaveDatatjeneste: OppgaveDatatjeneste) {
        this.sakDatatjeneste = sakDatatjeneste;
        this.oppgaveDatatjeneste = oppgaveDatatjeneste;
    }

    /** Returnerer angitt beslutning fra lokallager. Returnerer null dersom ikke funnet */
    hentFraLokallager(id: BeslutningId): BeslutningModell | null {
        return hentFraLokallagerEllerNull(this.beslutningLager, id);
    }

    /** Observerbar som leverer verdier når {@link #opprett} blir kalt */
    get vedNyBeslutning(): Observable<BeslutningModell> {
        return this.vedNyBeslutningSubject;
    }

    /** Returnerer beslutninger tilknyttet angitt sakId */
    finnTilknyttetSakFraLokallager(sakId: AgendaelementId): Observable<BeslutningModell[]> {
        return this.sakAbonnenter.observer(sakId,
          this.beslutningLager.pipe(
            selectManyByPredicate(forelderIdPredikat(sakId)),
            sorterLevertSamling(sorterBeslutning)
          ));
    }

    /** Returnerer beslutninger tilknyttet angitte sakIder */
    finnTilknyttetSakerFraLokallager(sakIder: AgendaelementId[]): Observable<BeslutningModell[]> {
        return this.sakAbonnenter.observer(sakIder,
          this.beslutningLager.pipe(
            selectManyByPredicate(foreldreIdPredikat(sakIder)),
            sorterLevertSamling(sorterBeslutning)
          ));
    }

    /** Erstatter alle beslutninger knytte til angitt sakId i lokallager */
    erstattBeslutningerTilknyttetSakILokallager(beslutninger: BeslutningModell[], sakId: AgendaelementId) {
        erstatteAlleEntiteterMedForelder(this.beslutningLager, beslutninger, sakId);
    }

    /** Sletter alle beslutninger knytte til angitt sakId i lokallager */
    slettBeslutningerTilknyttetSakILokallager(sakId: AgendaelementId) {
        slettAlleEntiteterMedForelder(this.beslutningLager, sakId);
    }

    /** Sletter alle beslutninger knyttet til angitte sakIder i lokallager */
    slettBeslutningerTilknyttetSakerILokallager(sakIder: AgendaelementId[]) {
        slettAlleEntiteterMedForeldre(this.beslutningLager, sakIder);
    }

    /** Oppretter ny beslutning */
    opprett(spesifikasjon: OpprettBeslutningSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<BeslutningModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.beslutningRessurs.opprett(spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  registrerEntitetMedForelder(this.beslutningLager, respons.body!, this.forelderIdFraBeslutning(respons.body!));
                  this.vedNyBeslutningSubject.next(respons.body!);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere beslutning')), 'opprett')
        }));
    }

    oppdater(id: BeslutningId, spesifikasjon: OppdaterBeslutningOgOppgaverSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<BeslutningModell>> {
        //håndteres som promise da kallet kun må skje én gang og kun én verdi skal returneres
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.beslutningRessurs.oppdater(id, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.beslutningLager, respons.body!, this.forelderIdFraBeslutning(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere beslutning')), 'oppdater')
        }));
    }

    /** Registrerer angitt beslutning som bruker favoritt dersom ikke allere markert som favoritt,
     * eller fjernes angitt beslutning som brukers favoritt  */
    async endreFavorittmarkering(beslutning: BeslutningModell): Promise<void> {
        const favoritterOppdatert = await this.favorittDatatjeneste.endreFavorittmarkering(beslutning, FavorittType.beslutning);
        this.beslutningLager.update(updateEntities(beslutning.id, {favoritter: favoritterOppdatert}));
    }

    async slett(id: BeslutningId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest({
            ressurskall: () => this.beslutningRessurs.slett(id).råRespons()
              .pipe(tap(respons => this.beslutningLager.update(deleteEntities(id)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette beslutning')), 'slett')
        }));
    }

    //hjelpemetoder

    private forelderIdFraBeslutning(beslutning: BeslutningModell): AgendaelementId {
        return beslutning.sakId!;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    /** Ved endringer i beslutning må tilknyttede oppgaver i {@link OppgaveDatatjeneste} oppdateres */
    private propagerEndringerTilAndreDatatjenester() {
        opprettEllerOppdatertHendelse(this.beslutningLager).subscribe(({hendelse, entiteter}) => {
            debug(`propagerer beslutningLager ${hendelse.type} til relatert datatjenester (oppg) for beslutningene ${hendelse.ids}`, hendelse, entiteter);
            entiteter
              .forEach(beslutning => {
                  this.oppgaveDatatjeneste.erstattOppgaverTilknyttetSakILokallager(beslutning.oppgaver, beslutning.id);
              });
        });

        slettHendelse(this.beslutningLager).subscribe(hendelse => {
            debug(`propagerer beslutningLager ${hendelse.type} til relatert datatjenester (oppg) for beslutningene ${hendelse.ids}`);
            this.oppgaveDatatjeneste.slettOppgaverTilknyttetBeslutningerILokallager(hendelse.ids);
        });
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNyBeslutning();
        this.lyttTilBeslutningOppdatert();
        this.lyttTilBeslutningSlettet();
    }

    private lyttTilNyBeslutning() {
        this.opprettetEllerOppdatertBeslutningHendelse<OppdateringmodellBeslutning>(ModelloppdateringType.NY_BESLUTNING)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          //unødvendig å hente beslutning dersom bruker ikke har tilgang til saksdetaljer
          .medFilter(modelloppdatering => this.harTilgangTilSaksdetaljer(modelloppdatering.modell))
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Ny beslutning registrert (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilBeslutningOppdatert() {
        this.opprettetEllerOppdatertBeslutningHendelse<OppdateringmodellBeslutning>(ModelloppdateringType.BESLUTNING_OPPDATERT)
          .medAbonnentoppslag(this.beslutningAbonnenter, hendelseModell => hendelseModell.beslutningId)
          .medAbonnentoppslagOpt(this.sakAbonnenter, hendelseModell => hendelseModell.sakId)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Beslutning har blitt oppdater (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilBeslutningSlettet() {
        this.slettetHendelse(ModelloppdateringType.BESLUTNING_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Beslutning har blitt slettet (av {bruker})'), i18nArgumenterMedAnsvarligBruker(oppdatering)))
          .oppdaterLagerVedHendelse();
    }


    private opprettetEllerOppdatertBeslutningHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellBeslutning>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, BeslutningModell>(this.beslutningLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.beslutningId)
          .medEntitetLaster(hendelseModell => this.beslutningRessurs.hent(hendelseModell.beslutningId).råRespons())
          .medForberedLageroppdatering(beslutning => leggTilForelderId(beslutning, this.forelderIdFraBeslutning(beslutning)));
    }

    private slettetHendelse(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<BeslutningId, BeslutningModell>(this.beslutningLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell);
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }

    private harTilgangTilSaksdetaljer(hendelseModell: OppdateringmodellBeslutning) {
        const sak = this.sakDatatjeneste.sakLager.value.entities[hendelseModell.sakId];
        return erIkkeNil(sak) ? harTilgangTilSaksdetaljer(sak, this.kontekst.autentisertBruker()) : true;
    }
}


/** Datalager-spesifikk representasjon av en beslutning der felter som inneholder data som håndteres av andre datalager er fjernet.
 * Dette da datalageret ikke garanterer at disse feltene er oppdatert.
 * Verdier for disse feltene må hentes via de respektive datalagrene for de aktuelle dataene. */
export type BeslutningModellDLM = Omit<BeslutningModell, 'oppgaver'>;
